import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Eye, Calendar, MapPin, Search, Star, StarOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const CATEGORIES = [
  { id: "ferias", name: "Ferias", icon: "🎪", color: "#F59E0B" },
  { id: "conciertos", name: "Conciertos", icon: "🎵", color: "#8B5CF6" },
  { id: "deportes", name: "Deportes", icon: "⚽", color: "#10B981" },
  { id: "municipal", name: "Municipal", icon: "🏛️", color: "#3B82F6" },
  { id: "religioso", name: "Religioso", icon: "⛪", color: "#EC4899" },
  { id: "escolar", name: "Escolar", icon: "🎓", color: "#06B6D4" },
  { id: "otro", name: "Otro", icon: "📅", color: "#6B7280" },
];

const initialFormState = {
  title: "",
  description: "",
  category: "otro",
  start_date: "",
  end_date: "",
  start_time: "",
  end_time: "",
  location: "",
  address: "",
  image_url: "",
  organizer: "",
  contact_phone: "",
  contact_email: "",
  website_url: "",
  is_featured: false,
  status: "published"
};

export default function AdminEventos() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [category, page, limit]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category !== "all") params.append("category", category);
      params.append("skip", String((page - 1) * limit));
      params.append("limit", String(limit));
      
      const response = await axios.get(`${API}/events?${params}`);
      setEvents(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setForm({
        title: event.title || "",
        description: event.description || "",
        category: event.category || "otro",
        start_date: event.start_date || "",
        end_date: event.end_date || "",
        start_time: event.start_time || "",
        end_time: event.end_time || "",
        location: event.location || "",
        address: event.address || "",
        image_url: event.image_url || "",
        organizer: event.organizer || "",
        contact_phone: event.contact_phone || "",
        contact_email: event.contact_email || "",
        website_url: event.website_url || "",
        is_featured: event.is_featured || false,
        status: event.status || "published"
      });
    } else {
      setEditingEvent(null);
      setForm(initialFormState);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.start_date) {
      toast.error("Título y fecha son requeridos");
      return;
    }

    setSaving(true);
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.event_id}`, form);
        toast.success("Evento actualizado");
      } else {
        await axios.post(`${API}/events`, form);
        toast.success("Evento creado");
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/events/${deleteId}`);
      toast.success("Evento eliminado");
      setDeleteId(null);
      fetchEvents();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const toggleFeatured = async (event) => {
    try {
      await axios.put(`${API}/events/${event.event_id}`, {
        ...event,
        is_featured: !event.is_featured
      });
      fetchEvents();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const getCategoryInfo = (catId) => {
    return CATEGORIES.find(c => c.id === catId) || CATEGORIES[CATEGORIES.length - 1];
  };

  const filtered = events.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return e.title?.toLowerCase().includes(s) || e.location?.toLowerCase().includes(s);
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-eventos">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Agenda de Eventos
            </h1>
            <p className="text-brand-textSecondary mt-1">
              {events.length} eventos
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="rounded-xl" data-testid="new-event-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Evento
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar eventos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay eventos</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map(event => {
              const catInfo = getCategoryInfo(event.category);
              return (
                <div key={event.event_id} className="bg-white rounded-xl border overflow-hidden" data-testid={`event-${event.event_id}`}>
                  {/* Image/Header */}
                  <div 
                    className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 relative flex items-center justify-center"
                    style={event.image_url ? {
                      backgroundImage: `url(${event.image_url})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    } : {}}
                  >
                    {!event.image_url && (
                      <span className="text-4xl">{catInfo.icon}</span>
                    )}
                    <Badge 
                      className="absolute top-2 left-2 text-white text-xs"
                      style={{ backgroundColor: catInfo.color }}
                    >
                      {catInfo.name}
                    </Badge>
                    {event.is_featured && (
                      <Badge className="absolute top-2 right-2 bg-amber-500 text-white text-xs">
                        Destacado
                      </Badge>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{event.title}</h3>
                    
                    <div className="space-y-1 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {event.start_date}
                        {event.start_time && ` ${event.start_time}`}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Eye className="h-3 w-3" />
                        {event.view_count || 0}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFeatured(event)}
                          className={event.is_featured ? "text-amber-500" : "text-gray-400"}
                        >
                          {event.is_featured ? <Star className="h-4 w-4 fill-current" /> : <StarOff className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(event)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteId(event.event_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Edit/Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEvent ? "Editar Evento" : "Nuevo Evento"}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Título *</label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Nombre del evento"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Categoría</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.icon} {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Estado</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="published">Publicado</SelectItem>
                      <SelectItem value="draft">Borrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Fecha inicio *</label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Fecha fin</label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Hora inicio</label>
                  <Input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Hora fin</label>
                  <Input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Descripción</label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    placeholder="Detalles del evento..."
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Lugar</label>
                  <Input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Ej: Parque Central"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Dirección</label>
                  <Input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Dirección completa"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">URL de imagen</label>
                  <Input
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Organizador</label>
                  <Input
                    value={form.organizer}
                    onChange={(e) => setForm({ ...form, organizer: e.target.value })}
                    placeholder="Nombre del organizador"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Teléfono</label>
                  <Input
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    placeholder="9999-9999"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    placeholder="email@ejemplo.com"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Sitio web</label>
                  <Input
                    value={form.website_url}
                    onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                <div className="col-span-2 flex items-center gap-3">
                  <Switch
                    checked={form.is_featured}
                    onCheckedChange={(v) => setForm({ ...form, is_featured: v })}
                  />
                  <label className="text-sm font-medium text-gray-700">Evento destacado</label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar evento?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="eventos-pagination"
          label="eventos"
        />
    </AdminLayout>
  );
}
